import {Injectable, signal} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {
  OrganizationListResponse,
  OrganizationResponse,
  OrganizationService,
  OrganizationUIConfig,
  UiConfigService,
} from '@middleware';
import {lastValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OrgService {
  private allOrganizations: OrganizationListResponse;
  private allOrgConfigs: OrganizationUIConfig[];

  organizationMap = signal<{[organization_id: string]: OrganizationResponse & OrganizationUIConfig}>({});
  organizationArray = signal<(OrganizationResponse & OrganizationUIConfig)[]>([]);

  constructor(
    private readonly uiConfigService: UiConfigService,
    private readonly organizationService: OrganizationService,
  ) {
    this.setOrganizations().catch(error => {
      console.error('Error loading organizations in AuthDataService:', error);
    });
  }

  /**
   * This is the organizationMap structure.
   * Generally, the config_data items are UI related, and are nested.
   *
   * config_data: { login_tile_logo, record_tile_logo, website_link }
   * data_enclave_url
   * email_domains
   * entity_id
   * full_name
   * id
   * name
   * organization_id
   * */

  /** setOrganizations: Loads org + config data from @middleware and merges into map.
   * Map is keyed by both middleware id and org name so lookups work
   * regardless of whether the caller has an id or a name (e.g. storage_organization.name from index search). */
  async setOrganizations() {
    this.allOrganizations = await lastValueFrom(this.organizationService.getOrganizations());
    this.allOrgConfigs = await lastValueFrom(this.uiConfigService.getOrganizationUIConfigs());

    const mergedOrgs = this.allOrganizations.organizations.map(org => {
      const uiConfig = this.allOrgConfigs.find(element => element.organization_id === org.id);
      return {...org, ...uiConfig} as OrganizationResponse & OrganizationUIConfig;
    });

    const orgMap = mergedOrgs.reduce(
      (acc, item) => {
        const mapKey = item.id;

        if (mapKey) {
          const valueObject = {...item};
          if (!item.organization_id) {
            valueObject.organization_id = item.id;
          }
          acc[mapKey] = valueObject;

          // Also key by name so storage_organization.name lookups work across middleware/backend
          if (item.name) {
            acc[item.name] = valueObject;
          }
        }

        return acc;
      },
      {} as {[organization_id: string]: OrganizationResponse & OrganizationUIConfig},
    );

    this.organizationMap.set(orgMap);
    this.organizationArray.set(mergedOrgs);
  }

  /** CONFIG DATA ITEMS */
  getDefaultOrganizationId(): string {
    const orgs = this.organizationArray();
    return orgs.length > 0 ? orgs[0].id : '';
  }

  getLandingServiceUrl(organization_id: string): string {
    return this.organizationMap()[organization_id]?.data_enclave_url ?? '';
  }

  getLoginTileLogo(organization_id: string): string {
    return this.organizationMap()[organization_id]?.config_data?.login_tile_logo ?? '';
  }

  getOrganizationFullName(organization_id: string): string {
    return this.organizationMap()[organization_id]?.full_name ?? '';
  }

  getRecordTileLogo(organization_id: string): string {
    return <string>this.organizationMap()[organization_id]?.config_data?.record_tile_logo ?? '';
  }

  getWebsiteLink(organization_id: string): string {
    return <string>this.organizationMap()[organization_id]?.config_data?.website_link ?? '';
  }

  getDisplayOrder(organization_id: string): string {
    return (<number>this.organizationMap()[organization_id]?.config_data?.display_order ?? 0).toString();
  }

  getLandingServiceEula(organization_id:string):SafeHtml {
    return <string>this.organizationMap()[organization_id]?.config_data?.pde_eula ?? '';
  }

  getStorageOrganizationNames(): string[] {
    return this.organizationArray().map(org => org.name);
  }
}
